import React, { useState, useEffect } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ObjectPropertyField from "./ObjectPropertyField";
import PropertyField from "../PropertyField";

interface ArrayPropertyFieldProps {
  items: any[];
  itemSchema?: any;
  onChange: (updated: any[]) => void;
  getNewItem?: () => any;
  sectionName?: string;
  parentPropName?: string;
}

// Componente para item ordenável
function SortableItem({
  id,
  index,
  item,
  isSimpleSchema,
  itemSchema,
  fullSectionName,
  handleRemove,
  handleItemChange,
}: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-gray-200 rounded p-2 bg-white ${isDragging ? "z-50 shadow-lg" : ""}`}
    >
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center space-x-2">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab hover:text-gray-700 text-gray-400"
          >
            <GripVertical className="w-4 h-4" />
          </div>
          <span className="text-xs font-medium text-gray-700">
            Item {index + 1}
          </span>
        </div>
        <button
          type="button"
          onClick={() => handleRemove(index)}
          className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100 flex items-center space-x-1"
        >
          <Trash2 className="w-3 h-3" />
          <span>Remover</span>
        </button>
      </div>
      {isSimpleSchema ? (
        <PropertyField
          propName={`item_${index}`}
          prop={{ ...(itemSchema as any), value: item }}
          sectionName={fullSectionName}
          arrayIndex={index}
          onChange={(value: any) => handleItemChange(index, value)}
        />
      ) : (
        <ObjectPropertyField
          schema={itemSchema?.props || itemSchema}
          values={item}
          sectionName={fullSectionName}
          arrayIndex={index}
          onChange={(updated: any) => handleItemChange(index, updated)}
        />
      )}
    </div>
  );
}

export default function ArrayPropertyField({
  items,
  itemSchema,
  onChange,
  getNewItem,
  sectionName,
  parentPropName,
}: ArrayPropertyFieldProps) {
  // Construir o sectionName completo combinando sectionName original com parentPropName
  // Isso garante que classes CSS sejam geradas corretamente para arrays dentro de objetos
  const fullSectionName =
    sectionName && parentPropName
      ? `${sectionName}_${parentPropName}`
      : sectionName || parentPropName;
  const isSimpleSchema =
    itemSchema &&
    typeof itemSchema === "object" &&
    typeof itemSchema.type === "string" &&
    !itemSchema.props;

  // Sensores para Dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Garantir que todos os itens tenham IDs
  // Se o item for um objeto, adicionamos a propriedade id
  // Se for primitivo (simple schema), não podemos adicionar id diretamente no valor,
  // então precisaríamos de um wrapper ou map, mas o request do usuário diz "criando novos itens... no json",
  // sugerindo que são objetos. Vamos focar em objetos por enquanto.
  useEffect(() => {
    if (!items) return;

    let hasChanges = false;
    const itemsWithIds = items.map((item) => {
      // Se for objeto e não tiver ID, adiciona
      if (item && typeof item === "object" && !item.id) {
        hasChanges = true;
        return { ...item, id: crypto.randomUUID() };
      }
      return item;
    });

    if (hasChanges) {
      console.log("🔄 Adicionando IDs aos itens do array para ordenação");
      onChange(itemsWithIds);
    }
  }, [items, onChange]);

  const handleItemChange = (index: number, incoming: any) => {
    console.log("🔄 ArrayPropertyField handleItemChange:", { index, incoming });

    let updatedValue: any;
    if (
      incoming &&
      typeof incoming === "object" &&
      Object.prototype.hasOwnProperty.call(incoming, "value")
    ) {
      const { value } = incoming;
      updatedValue = value;
    } else {
      updatedValue = incoming;
    }

    console.log("📝 ArrayPropertyField updatedValue:", updatedValue);

    const next = [...(items || [])];
    next[index] = updatedValue;
    onChange(next);
  };

  const buildDefaultValue = (fieldCfg: any): any => {
    if (!fieldCfg || typeof fieldCfg !== 'object') return '';
    const t = fieldCfg.type;
    if (t === 'array') return [];
    if (t === 'object') {
      if (fieldCfg.props && typeof fieldCfg.props === 'object') {
        const obj: any = {};
        for (const [k, v] of Object.entries(fieldCfg.props)) {
          obj[k] = buildDefaultValue(v);
        }
        return obj;
      }
      return {};
    }
    if (t === 'image') {
      return {
        desktop: { value: '', type: 'image' },
        mobile: { value: '', type: 'image' },
        link: { value: '' },
      };
    }
    if (t === 'button') {
      return {
        text: { value: '' },
        link: { value: '' },
        style: 'background-color: #000000; color: #ffffff; padding: 12px 24px; font-size: 16px; font-weight: 600;',
      };
    }
    if (t === 'color') return { style: '' };
    if (t === 'text' || t === 'id' || t === 'select' || t === 'date' || t === 'link') return { value: '' };
    return '';
  };

  const handleAdd = () => {
    let newItem: any;
    const newItemId = crypto.randomUUID();

    if (getNewItem) {
      newItem = getNewItem();
    } else if (itemSchema) {
      if (isSimpleSchema) {
        newItem = buildDefaultValue(itemSchema);
      } else if (itemSchema.props && typeof itemSchema.props === 'object') {
        newItem = { id: newItemId };
        for (const [k, v] of Object.entries(itemSchema.props)) {
          newItem[k] = buildDefaultValue(v);
        }
      } else {
        newItem = { id: newItemId };
      }
    } else {
      newItem = "";
    }

    if (newItem && typeof newItem === "object" && !newItem.id) {
      newItem.id = newItemId;
    }

    onChange([...(items || []), newItem]);
  };

  const handleRemove = (index: number) => {
    const next = [...(items || [])];
    next.splice(index, 1);
    onChange(next);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = (items || []).findIndex(
        (item) => (item.id || item) === active.id,
      );
      const newIndex = (items || []).findIndex(
        (item) => (item.id || item) === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        // Mover usando arrayMove do dnd-kit seria ideal, mas precisamos importar
        // Vamos fazer manualmente para evitar dependência extra se arrayMove não estiver exportado onde pensamos
        // Mas @dnd-kit/sortable exporta arrayMove, vamos usar
        const newItems = [...(items || [])];
        const [movedItem] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, movedItem);

        onChange(newItems);
      }
    }
  };

  // Preparar lista de IDs para o SortableContext
  // Usar item.id se existir, ou o próprio item se for primitivo (pode falhar se houver duplicatas)
  // Fallback para índice não é ideal para dnd, mas...
  const itemIds = (items || []).map((item, index) =>
    item && typeof item === "object" && item.id
      ? item.id
      : `legacy-id-${index}`,
  );

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-700">
          Itens ({items?.length || 0})
        </span>
        <button
          type="button"
          onClick={handleAdd}
          className="px-2 py-1 text-xs bg-gradient-primary text-white rounded hover:opacity-90 flex items-center space-x-1"
        >
          <Plus className="w-3 h-3" />
          <span>Adicionar</span>
        </button>
      </div>

      {(items || []).length === 0 && (
        <div className="text-xs text-gray-500">Nenhum item adicionado</div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {(items || []).map((item, index) => {
              // ID para o useSortable
              const id =
                item && typeof item === "object" && item.id
                  ? item.id
                  : `legacy-id-${index}`;

              return (
                <SortableItem
                  key={id}
                  id={id}
                  index={index}
                  item={item}
                  isSimpleSchema={isSimpleSchema}
                  itemSchema={itemSchema}
                  fullSectionName={fullSectionName}
                  handleRemove={handleRemove}
                  handleItemChange={handleItemChange}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
